import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { adminAPI } from '../../services/admin';

interface AnalysisSession {
  id: string;
  userId: string;
  userEmail: string;
  documentPreview: string;
  status: string;
  searchRoundsCompleted: number;
  totalJudgmentsFound: number;
  createdAt: string;
  completedAt: string | null;
  evaluation: {
    score: number;
    grade: string;
    legalBasesCoverage: number;
    authorityScore: number;
    balanceScore: number;
    evaluatedAt: string;
  } | null;
  feedback: {
    rating: number;
    comment: string | null;
    submittedAt: string;
  } | null;
}

interface AnalysisStats {
  sessions: {
    total: number;
    completed: number;
    failed: number;
    today: number;
    thisWeek: number;
    avgDurationSeconds: number;
    avgSearchRounds: number;
    avgJudgmentsFound: number;
  };
  evaluations: {
    total: number;
    avgScore: number;
    avgLegalCoverage: number;
    avgAuthority: number;
    avgBalance: number;
    gradeDistribution: Record<string, number>;
  };
  feedback: {
    total: number;
    avgRating: number;
    ratingDistribution: Record<number, number>;
  };
  correlation: {
    scoreRatingCorrelation: number | null;
  };
}

function GradeBadge({ grade }: { grade: string }) {
  const colors: Record<string, string> = {
    A: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    B: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    C: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    D: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    F: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${colors[grade] || colors.F}`}>
      {grade}
    </span>
  );
}

function RatingStars({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`w-4 h-4 ${star <= rating ? 'text-yellow-400' : 'text-slate-300 dark:text-slate-600'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

function StatCard({ title, value, subtitle, color = 'blue' }: { title: string; value: string | number; subtitle?: string; color?: string }) {
  const colors: Record<string, string> = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
    green: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
    yellow: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800',
    purple: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800',
  };
  return (
    <div className={`rounded-lg border p-4 ${colors[color]}`}>
      <div className="text-sm text-slate-600 dark:text-slate-400">{title}</div>
      <div className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{value}</div>
      {subtitle && <div className="text-xs text-slate-500 dark:text-slate-500 mt-1">{subtitle}</div>}
    </div>
  );
}

export default function AdminDeepAnalysisPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'pl' ? 'pl-PL' : 'en-US';
  const [stats, setStats] = useState<AnalysisStats | null>(null);
  const [sessions, setSessions] = useState<AnalysisSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedSession, setSelectedSession] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [statsData, sessionsData] = await Promise.all([
          adminAPI.getAnalysisStats(),
          adminAPI.getAnalysisSessions({ page, limit: 10 }),
        ]);
        setStats(statsData);
        setSessions(sessionsData.sessions);
        setTotalPages(sessionsData.pagination.totalPages);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [page]);

  if (isLoading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{t('adminDeepAnalysis.title')}</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">{t('adminDeepAnalysis.subtitle')}</p>
      </div>

      {/* Stats Overview */}
      {stats && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard
              title={t('adminDeepAnalysis.sessionsToday')}
              value={stats.sessions.today}
              subtitle={t('adminDeepAnalysis.thisWeek', { count: stats.sessions.thisWeek })}
              color="blue"
            />
            <StatCard
              title={t('adminDeepAnalysis.averageScore')}
              value={stats.evaluations.avgScore.toFixed(1)}
              subtitle={t('adminDeepAnalysis.outOfRatings', { count: stats.evaluations.total })}
              color="green"
            />
            <StatCard
              title={t('adminDeepAnalysis.averageRating')}
              value={`${stats.feedback.avgRating.toFixed(1)}/5`}
              subtitle={t('adminDeepAnalysis.outOfOpinions', { count: stats.feedback.total })}
              color="yellow"
            />
            <StatCard
              title={t('adminDeepAnalysis.correlation')}
              value={stats.correlation.scoreRatingCorrelation !== null
                ? stats.correlation.scoreRatingCorrelation.toFixed(2)
                : 'N/A'}
              subtitle={t('adminDeepAnalysis.scoreVsRating')}
              color="purple"
            />
          </div>

          {/* Grade Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">{t('adminDeepAnalysis.gradeDistribution')}</h2>
              <div className="space-y-3">
                {(['A', 'B', 'C', 'D', 'F'] as const).map((grade) => {
                  const count = stats.evaluations.gradeDistribution[grade] || 0;
                  const total = stats.evaluations.total || 1;
                  const percentage = (count / total) * 100;
                  return (
                    <div key={grade} className="flex items-center gap-3">
                      <GradeBadge grade={grade} />
                      <div className="flex-1 bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                        <div
                          className="bg-blue-500 h-2 rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-sm text-slate-600 dark:text-slate-400 w-12 text-right">
                        {count}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">{t('adminDeepAnalysis.ratingDistribution')}</h2>
              <div className="space-y-3">
                {[5, 4, 3, 2, 1].map((rating) => {
                  const count = stats.feedback.ratingDistribution[rating] || 0;
                  const total = stats.feedback.total || 1;
                  const percentage = (count / total) * 100;
                  return (
                    <div key={rating} className="flex items-center gap-3">
                      <RatingStars rating={rating} />
                      <div className="flex-1 bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                        <div
                          className="bg-yellow-400 h-2 rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-sm text-slate-600 dark:text-slate-400 w-12 text-right">
                        {count}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Average Metrics */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 mb-8">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">{t('adminDeepAnalysis.averageMetrics')}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-sm text-slate-500 dark:text-slate-400">{t('adminDeepAnalysis.legalCoverage')}</div>
                <div className="text-xl font-semibold text-slate-900 dark:text-white">
                  {stats.evaluations.avgLegalCoverage.toFixed(1)}%
                </div>
              </div>
              <div>
                <div className="text-sm text-slate-500 dark:text-slate-400">{t('adminDeepAnalysis.sourceAuthority')}</div>
                <div className="text-xl font-semibold text-slate-900 dark:text-white">
                  {stats.evaluations.avgAuthority.toFixed(1)}%
                </div>
              </div>
              <div>
                <div className="text-sm text-slate-500 dark:text-slate-400">{t('adminDeepAnalysis.perspectiveBalance')}</div>
                <div className="text-xl font-semibold text-slate-900 dark:text-white">
                  {stats.evaluations.avgBalance.toFixed(1)}%
                </div>
              </div>
              <div>
                <div className="text-sm text-slate-500 dark:text-slate-400">{t('adminDeepAnalysis.avgAnalysisTime')}</div>
                <div className="text-xl font-semibold text-slate-900 dark:text-white">
                  {stats.sessions.avgDurationSeconds
                    ? `${Math.round(stats.sessions.avgDurationSeconds / 60)}min`
                    : 'N/A'}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Sessions Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('adminDeepAnalysis.sessionsList')}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-900/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('common.date')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('adminUsers.user')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('common.document')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('common.status')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('adminDeepAnalysis.evaluation')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('adminDeepAnalysis.rating')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {sessions.map((session) => (
                <tr
                  key={session.id}
                  className="hover:bg-slate-50 dark:hover:bg-slate-900/30 cursor-pointer"
                  onClick={() => setSelectedSession(selectedSession === session.id ? null : session.id)}
                >
                  <td className="px-4 py-3 text-sm text-slate-900 dark:text-white">
                    {new Date(session.createdAt).toLocaleDateString(locale, {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                    {session.userEmail || t('common.anonymous')}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 max-w-xs truncate">
                    {session.documentPreview || '-'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      session.status === 'completed'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : session.status === 'failed'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                    }`}>
                      {session.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {session.evaluation ? (
                      <div className="flex items-center gap-2">
                        <GradeBadge grade={session.evaluation.grade} />
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          {session.evaluation.score}
                        </span>
                      </div>
                    ) : (
                      <span className="text-slate-400 dark:text-slate-500">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {session.feedback ? (
                      <RatingStars rating={session.feedback.rating} />
                    ) : (
                      <span className="text-slate-400 dark:text-slate-500">-</span>
                    )}
                  </td>
                </tr>
              ))}
              {sessions.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                    {t('adminDeepAnalysis.noSessions')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 text-sm rounded border border-slate-300 dark:border-slate-600 disabled:opacity-50"
            >
              {t('common.previous')}
            </button>
            <span className="text-sm text-slate-600 dark:text-slate-400">
              {t('common.pageXofY', { page, total: totalPages })}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 text-sm rounded border border-slate-300 dark:border-slate-600 disabled:opacity-50"
            >
              {t('common.next')}
            </button>
          </div>
        )}
      </div>

      {/* Session Details Modal */}
      {selectedSession && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{t('adminDeepAnalysis.sessionDetails')}</h3>
              <button
                onClick={() => setSelectedSession(null)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              {(() => {
                const session = sessions.find((s) => s.id === selectedSession);
                if (!session) return <p>{t('adminDeepAnalysis.sessionNotFound')}</p>;
                return (
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-slate-500 dark:text-slate-400">{t('adminDeepAnalysis.sessionId')}</div>
                      <div className="font-mono text-sm">{session.id}</div>
                    </div>
                    <div>
                      <div className="text-sm text-slate-500 dark:text-slate-400">{t('common.document')}</div>
                      <div className="text-sm bg-slate-100 dark:bg-slate-900 p-3 rounded mt-1">
                        {session.documentPreview || t('adminDeepAnalysis.noPreview')}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm text-slate-500 dark:text-slate-400">{t('adminDeepAnalysis.searchRounds')}</div>
                        <div className="font-semibold">{session.searchRoundsCompleted}</div>
                      </div>
                      <div>
                        <div className="text-sm text-slate-500 dark:text-slate-400">{t('adminDeepAnalysis.foundJudgments')}</div>
                        <div className="font-semibold">{session.totalJudgmentsFound}</div>
                      </div>
                    </div>
                    {session.evaluation && (
                      <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                        <h4 className="font-semibold mb-2">{t('adminDeepAnalysis.automaticEvaluation')}</h4>
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <div>
                            <span className="text-slate-500">{t('adminDeepAnalysis.score')}</span>{' '}
                            <strong>{session.evaluation.score}</strong>
                          </div>
                          <div>
                            <span className="text-slate-500">{t('adminDeepAnalysis.coverage')}</span>{' '}
                            <strong>{session.evaluation.legalBasesCoverage}%</strong>
                          </div>
                          <div>
                            <span className="text-slate-500">{t('adminDeepAnalysis.authority')}</span>{' '}
                            <strong>{session.evaluation.authorityScore}%</strong>
                          </div>
                        </div>
                      </div>
                    )}
                    {session.feedback && (
                      <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                        <h4 className="font-semibold mb-2">{t('adminDeepAnalysis.userFeedback')}</h4>
                        <div className="flex items-center gap-2 mb-2">
                          <RatingStars rating={session.feedback.rating} />
                        </div>
                        {session.feedback.comment && (
                          <div className="text-sm bg-slate-100 dark:bg-slate-900 p-3 rounded">
                            {session.feedback.comment}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
